import { ColumnsType } from 'antd/es/table';
import { memoize } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ProjectTemplateData,
  setProjectTemplateActiveRecord,
} from 'data/templates/projects';
import {
  updatePhaseInProjectTemplate,
  updateTaskInProjectTemplate,
} from 'data/templates/projects/actions';
import { useTranslation } from 'hooks/use-translation';
import { StatusNameCell } from './status-name-cell';

export const useProjectTemplateColumns =
  (): ColumnsType<ProjectTemplateData> => {
    const dispatch = useDispatch();
    const nameText = useTranslation('name');
    const startOnText = useTranslation('templates.starts_on');
    const durationText = useTranslation('templates.duration');
    const [autoFocus, setAutoFocus] = useState(false);
    const update = useCallback(
      ({ duration = '0', id, startDay = '0', type }) => {
        if (type === 'PROJECT_PHASE') {
          dispatch(
            updatePhaseInProjectTemplate({
              projectPhaseId: id,
              payload: {
                duration: parseInt(duration, 10),
                startDay: parseInt(startDay, 10),
              },
            }),
          );
        } else {
          dispatch(
            updateTaskInProjectTemplate({
              taskId: id,
              payload: {
                duration: parseInt(duration, 10),
                startDay: parseInt(startDay, 10),
              },
            }),
          );
        }
      },
      [dispatch],
    );

    const onActivateRow = useCallback(
      (row: ProjectTemplateData) => {
        dispatch(setProjectTemplateActiveRecord(row));
      },
      [dispatch],
    );

    const performAutoFocus = useCallback(() => setAutoFocus(true), []);

    const onClick = useMemo(
      () => memoize((data) => () => onActivateRow(data)),
      [onActivateRow],
    );

    return useMemo(
      () =>
        [
          {
            title: nameText,
            dataIndex: 'name',
            key: 'name',
            width: '375px',
            render: (_, data: ProjectTemplateData, index: number) => (
              <StatusNameCell
                index={index}
                parentTotal={data.parentTotal}
                data={data}
                autoFocus={autoFocus}
                performAutoFocus={performAutoFocus}
                onClick={onClick(data)}
              />
            ),
          },
          {
            title: startOnText,
            dataIndex: 'startDay',
            key: 'startDay',
            width: '89px',
            editable: true,
            ellipsis: true,
          },
          {
            title: durationText,
            dataIndex: 'duration',
            key: 'duration',
            editable: true,
            ellipsis: true,
          },
        ].map((col) => {
          if (!col.editable) {
            return col;
          }

          return {
            ...col,
            onCell: (record) => ({
              record,
              editable: col.editable,
              dataIndex: col.dataIndex,
              title: col.title,
              update,
            }),
          };
        }),
      [
        autoFocus,
        durationText,
        nameText,
        onClick,
        performAutoFocus,
        startOnText,
        update,
      ],
    );
  };
